/**
 * @file: useMediaFilePicker.web.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { MediaResultEx } from '@euler/app/flows/media/types';
import { isMobileBrowser } from '@euler/functions/responsiveness';
import { delayed } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCallback, useEffect } from 'react';

type MediaType = 'photo' | 'video' | 'any';

const FilePickerOptions: {
  [p in MediaType]: {
    allowedExtensions: RegExp;
    errorMessage: string;
    mimeTypes: string;
  };
} = {
  photo: {
    allowedExtensions: /\.(jpe?g|png)$/i,
    errorMessage:
      'Unsupported image file, where only JPEG or PNG files are allowed!',
    mimeTypes: 'image/png, image/jpeg',
  },
  video: {
    allowedExtensions: /\.(mp4|mov|m4v)$/i,
    errorMessage:
      'Unsupported video file, where only MP4 video files are allowed!',
    mimeTypes: 'video/mp4',
  },
  any: {
    allowedExtensions: /\.(mp4|mov|m4v|jpe?g|png)$/i,
    errorMessage:
      'Unsupported media file, where only JPEG, PNG or MP4 video files are allowed!',
    mimeTypes: 'image/png, image/jpeg, video/mp4',
  },
};

export const withMediaFilePicker = (
  type: MediaType,
  multiple?: boolean,
  mediaCaptureRequired?: boolean,
) => {
  let lock = false;

  return new Promise<File[]>((resolve, reject) => {
    // create input file
    const el = document.createElement('input');
    el.id = (+new Date()).toString();
    el.style.display = 'none';
    el.setAttribute('accept', FilePickerOptions[type].mimeTypes);
    el.setAttribute('type', 'file');
    if (mediaCaptureRequired) {
      el.setAttribute('capture', 'environment'); // use back camera on mobile
    }
    el.multiple = multiple ?? false;
    document.body.appendChild(el);

    const cleanup = () => {
      const node = document.getElementById(el.id);
      node && document.body.removeChild(node);
    };

    if (isMobileBrowser()) {
      el.addEventListener('cancel', () => {
        cleanup();
        reject();
      });
    } else {
      el.addEventListener('click', () => {
        // file blur
        window.addEventListener(
          'focus',
          () => {
            delayed(() => {
              if (!lock && document.getElementById(el.id)) {
                reject(new Error('onblur'));
                // remove dom
                document.body.removeChild(document.getElementById(el.id)!);
              }
            }, 1000);
          },
          { once: true },
        );
      });
    }

    el.addEventListener(
      'change',
      () => {
        lock = true;

        try {
          if (!el.files?.length) {
            reject();
            return;
          }

          const files: File[] = [];

          // eslint-disable-next-line @typescript-eslint/prefer-for-of
          for (let i = 0; i < el.files.length; i++) {
            const file = el.files[i];
            if (
              file?.name != null &&
              !FilePickerOptions[type].allowedExtensions.test(file.name)
            ) {
              alert(FilePickerOptions[type].errorMessage);
              if (!multiple) {
                reject('Unsupported file type');
                return;
              }
            } else {
              files.push(file);
            }
          }

          if (!files.length) {
            reject();
            return;
          }

          resolve(files);
        } finally {
          cleanup();
        }
      },
      { once: true },
    );

    // open file select box
    el.click();
  });
};

async function resolveImageSize(uri: any) {
  return await new Promise<{ width: number; height: number }>(
    (resolve, reject) => {
      const image = new Image();
      image.onload = () => {
        resolve({ width: image.naturalWidth, height: image.naturalHeight });
      };
      image.onerror = e => {
        console.error(e);
        reject(e);
      };
      image.src = uri;
    },
  );
}

export const useMediaFilePicker = ({
  photoOnly,
  videoOnly,
  multiple,
  max,
  enabled = true,
  mediaCaptureRequired,
  onSelected,
}: {
  photoOnly?: boolean;
  videoOnly?: boolean;
  multiple?: boolean;
  max?: number;
  enabled?: boolean;
  mediaCaptureRequired?: boolean;
  onSelected?: (results: MediaResultEx[]) => void;
}) => {
  const navigation = useNavigation<StackNavigationProp<any>>();

  const handleFiles = usePersistFn(async (files: File[]) => {
    const results: MediaResultEx[] = [];

    for (const file of files) {
      const uri = URL.createObjectURL(file);
      const filename = file.name;
      const type =
        photoOnly || FilePickerOptions.photo.allowedExtensions.test(filename)
          ? 'photo'
          : 'video';
      if (type === 'photo') {
        const { width, height } = await resolveImageSize(uri);
        results.push({
          type,
          contentType: file.type,
          uri,
          filename,
          file: {
            isRawPhoto: false,
            width,
            height,
            metadata: {} as any,
            path: filename,
          },
          previewRect: { x: 0, y: 0, width, height },
          mask: { width, height },
          cropRect: { x: 0, y: 0, width, height },
        });
      } else {
        results.push({
          type,
          contentType:
            file.type === 'video/quicktime' ? 'video/mp4' : file.type,
          filename,
          uri,
          file: {
            duration: 0,
            path: filename,
          },
        });
      }
    }

    onSelected?.(max ? results.slice(0, max) : results);
  });

  useEffect(() => {
    if (!enabled) return;

    withMediaFilePicker(
      photoOnly ? 'photo' : videoOnly ? 'video' : 'any',
      multiple,
      mediaCaptureRequired,
    ).then(handleFiles, () => {
      navigation.pop();
    });
  }, [
    enabled,
    handleFiles,
    mediaCaptureRequired,
    multiple,
    navigation,
    photoOnly,
    videoOnly,
  ]);

  const onDismiss = useCallback(() => {
    navigation.pop();
  }, [navigation]);

  return { onDismiss };
};
